import React, { createContext, ReactElement } from 'react'
import { ControllerRenderProps, FieldValues, useController, useFormContext } from 'react-hook-form'
import { useEffectOnce } from 'react-use'

import { RCFC } from 'utils/types'
import { cn } from 'utils'
import { ErrorMessage } from 'components/error-message'
import { LabelProps } from 'components/label/label'

import { BaseRadioFormFieldProps } from './BaseRadioFormField'
import labelStyles from '../../label/label.module.css'
import baseClasses from '../form-field.module.css'
import classes from './base-radio.module.css'

type GroupRadioTheme = 'default' | 'pills' | 'vertical'
interface GroupRadioFormFieldProps {
  groupTitle?: string
  fieldName: string
  className?: string
  classNameOptions?: string
  defaultValue?: any
  labelId?: string
  theme?: GroupRadioTheme
  variation?: LabelProps['variation']
  description?: JSX.Element | string | null
  defaultFirstItemChecked?: boolean
  hideErrors?: boolean
}

export const RadiGroupFormFieldContext = createContext<ControllerRenderProps<
  FieldValues,
  string
> | null>(null)

const _GroupRadioFormField: RCFC<
  GroupRadioFormFieldProps,
  ReactElement<BaseRadioFormFieldProps>[]
> = ({
  groupTitle,
  children,
  fieldName,
  className,
  classNameOptions,
  defaultValue,
  labelId,
  variation,
  description,
  theme = 'default',
  defaultFirstItemChecked = true,
  hideErrors,
}) => {
  const { setValue } = useFormContext()

  const {
    field,
    fieldState: { error },
  } = useController({ name: fieldName, shouldUnregister: true })

  useEffectOnce(() => {
    if (field.value && field.value !== defaultValue) return

    if (defaultValue) {
      setValue(fieldName, defaultValue)
      return
    }

    if (defaultFirstItemChecked) {
      const firstChild = React.Children.toArray(children)[0] as
        | ReactElement<BaseRadioFormFieldProps>
        | undefined

      if (firstChild) {
        setValue(fieldName, firstChild.props.value)
      }
    }
  })

  return (
    <RadiGroupFormFieldContext.Provider value={field}>
      <div
        className={cn(baseClasses.container, className)}
        aria-labelledby={labelId}
        role="radiogroup"
      >
        {groupTitle && (
          <div
            className={cn(
              classes.legend,
              baseClasses.label,
              labelStyles.labelText,
              variation && labelStyles[variation],
              baseClasses.labelText,
            )}
          >
            {groupTitle}
          </div>
        )}
        {description && (
          <div className={cn(classes.description, baseClasses.description)}>{description}</div>
        )}

        <div className={cn(classes[theme], classNameOptions)}>{children}</div>

        {!hideErrors && error?.message && (
          <ErrorMessage className={classes.error}>{error.message}</ErrorMessage>
        )}
      </div>
    </RadiGroupFormFieldContext.Provider>
  )
}

export const RadioFormFieldGroup = _GroupRadioFormField
